import type { SVGProps } from "react";
import { motion } from "motion/react";
import { cn } from "../cn";

export interface IllustrationProps extends SVGProps<SVGSVGElement> {
  size?: number | string;
  className?: string;
}

// ============================================================================
// 1. EMPTY STATE ILLUSTRATION (Lévitation isometric box + éléments flottants)
// ============================================================================

export function EmptyStateIllustration({ size = 160, className, style, ...props }: IllustrationProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      <motion.ellipse
        cx="80"
        cy="142"
        rx="40"
        ry="8"
        fill="#64748b"
        fillOpacity="0.2"
        animate={{ scale: [1, 0.75, 1], opacity: [0.3, 0.15, 0.3] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.g animate={{ y: [-7, 7, -7] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}>
        <polygon points="80,45 125,70 80,95 35,70" fill="#6366f1" fillOpacity="0.2" stroke="#6366f1" strokeWidth="2" strokeLinejoin="round" />
        <polygon points="35,70 80,95 80,130 35,105" fill="#6366f1" fillOpacity="0.4" stroke="#6366f1" strokeWidth="2" strokeLinejoin="round" />
        <polygon points="80,95 125,70 125,105 80,130" fill="#6366f1" fillOpacity="0.6" stroke="#6366f1" strokeWidth="2" strokeLinejoin="round" />
        <polygon points="80,45 105,30 125,70 80,95" fill="#6366f1" fillOpacity="0.15" stroke="#6366f1" strokeWidth="1.5" />
        <motion.g animate={{ y: [-3, -12, -3], rotate: [-5, 5, -5] }} transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}>
          <rect x="66" y="25" width="28" height="18" rx="3" fill="#ffffff" stroke="#6366f1" strokeWidth="1.75" className="dark:fill-neutral-900" />
          <path d="M66 25L80 35L94 25" stroke="#6366f1" strokeWidth="1.5" strokeLinecap="round" />
        </motion.g>
        <motion.path d="M30 45l2 4 4 2-4 2-2 4-2-4-4-2 4-2z" fill="#fbbf24" animate={{ rotate: [0, 180, 360], scale: [0.8, 1.2, 0.8] }} transition={{ duration: 4, repeat: Infinity, ease: "linear" }} style={{ transformOrigin: "30px 45px" }} />
        <motion.circle cx="135" cy="40" r="3.5" fill="#818cf8" animate={{ scale: [1, 1.5, 1], opacity: [0.4, 1, 0.4] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }} />
      </motion.g>
    </svg>
  );
}

// ============================================================================
// 2. CELEBRATION ILLUSTRATION (Rayons tournants + Trophée & Confettis)
// ============================================================================

export function CelebrationIllustration({ size = 160, className, style, ...props }: IllustrationProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      <motion.g animate={{ rotate: [0, 360] }} transition={{ duration: 25, repeat: Infinity, ease: "linear" }} style={{ transformOrigin: "80px 80px" }} opacity="0.25">
        <path d="M80 15L85 65L80 80L75 65Z" fill="#f59e0b" />
        <path d="M80 145L85 95L80 80L75 95Z" fill="#f59e0b" />
        <path d="M15 80L65 85L80 80L65 75Z" fill="#f59e0b" />
        <path d="M145 80L95 85L80 80L95 75Z" fill="#f59e0b" />
      </motion.g>
      <motion.circle cx="80" cy="80" r="45" stroke="#fbbf24" strokeWidth="2" strokeOpacity="0.4" fill="none" animate={{ scale: [0.95, 1.1, 0.95], opacity: [0.3, 0.7, 0.3] }} transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }} />
      <motion.g animate={{ y: [-3, 3, -3], scale: [1, 1.03, 1] }} transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}>
        <path d="M65 95L55 135L75 125L80 128L85 125L105 135L95 95" fill="#f43f5e" />
        <circle cx="80" cy="75" r="32" fill="#fbbf24" stroke="#d97706" strokeWidth="3" />
        <polygon points="80,57 85,69 98,71 88,80 91,93 80,86 69,93 72,80 62,71 75,69" fill="#fffbeb" />
      </motion.g>
      <motion.circle cx="35" cy="40" r="4" fill="#fb7185" animate={{ y: [-5, 5, -5], opacity: [0.4, 1, 0.4] }} transition={{ duration: 1.8, repeat: Infinity }} />
      <motion.rect x="120" y="45" width="6" height="6" rx="1" fill="#38bdf8" animate={{ rotate: [0, 180], y: [5, -5, 5] }} transition={{ duration: 2.2, repeat: Infinity }} />
    </svg>
  );
}

// ============================================================================
// 3. PROCESSING CLOUD ILLUSTRATION (Fixé & Net - Nuage Blanc / Dark + IA Accent)
// ============================================================================

export function ProcessingCloudIllustration({ size = 160, className, style, ...props }: IllustrationProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      <motion.g animate={{ rotate: [0, 360] }} transition={{ duration: 8, repeat: Infinity, ease: "linear" }} style={{ transformOrigin: "80px 75px" }}>
        <circle cx="80" cy="75" r="50" fill="none" stroke="#6366f1" strokeWidth="2" strokeDasharray="8 6" strokeOpacity="0.4" />
        <circle cx="130" cy="75" r="5" fill="#6366f1" />
        <circle cx="30" cy="75" r="4" fill="#818cf8" />
      </motion.g>
      <motion.g animate={{ scale: [0.98, 1.02, 0.98], y: [-2, 2, -2] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}>
        <path d="M125 105H45a25 25 0 0 1-5-49.5 35 35 0 0 1 67-10.5 28 28 0 0 1 18 60Z" fill="#ffffff" stroke="#1e293b" strokeWidth="2.5" strokeLinejoin="round" className="dark:fill-neutral-900 dark:stroke-neutral-100" />
        <motion.g animate={{ rotate: [0, 360] }} transition={{ duration: 10, repeat: Infinity, ease: "linear" }} style={{ transformOrigin: "80px 75px" }}>
          <circle cx="80" cy="75" r="11" fill="none" stroke="#6366f1" strokeWidth="2.5" strokeDasharray="4 2" />
          <circle cx="80" cy="75" r="4" fill="#6366f1" />
        </motion.g>
        <motion.line x1="60" y1="110" x2="60" y2="128" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" animate={{ y: [0, 6, 0], opacity: [0.2, 1, 0.2] }} transition={{ duration: 1.5, repeat: Infinity }} />
        <motion.line x1="80" y1="110" x2="80" y2="135" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" animate={{ y: [0, 8, 0], opacity: [0.4, 1, 0.4] }} transition={{ duration: 1.8, repeat: Infinity, delay: 0.3 }} />
        <motion.line x1="100" y1="110" x2="100" y2="124" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" animate={{ y: [0, 5, 0], opacity: [0.2, 1, 0.2] }} transition={{ duration: 1.4, repeat: Infinity, delay: 0.6 }} />
      </motion.g>
    </svg>
  );
}

// ============================================================================
// 4. SECURITY SCAN ILLUSTRATION (Bouclier avec balayage laser continu)
// ============================================================================

export function SecurityScanIllustration({ size = 160, className, style, ...props }: IllustrationProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      <motion.path d="M80 145s45-22 45-60V40l-45-18-45 18v45c0 38 45 60 45 60z" fill="#10b981" fillOpacity="0.1" stroke="#10b981" strokeWidth="1.5" animate={{ scale: [1, 1.05, 1], opacity: [0.3, 0.7, 0.3] }} transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }} style={{ transformOrigin: "80px 80px" }} />
      <path d="M80 135s38-18 38-52V45l-38-15-38 15v38c0 34 38 52 38 52z" fill="#ffffff" stroke="#1e293b" strokeWidth="3" strokeLinejoin="round" className="dark:fill-neutral-900 dark:stroke-neutral-100" />
      <path d="M65 78L75 88L96 66" stroke="#10b981" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <motion.g animate={{ y: [35, 120, 35] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}>
        <line x1="38" y1="0" x2="122" y2="0" stroke="#34d399" strokeWidth="2.5" />
        <polygon points="38,0 122,0 110,-12 50,-12" fill="#34d399" fillOpacity="0.2" />
      </motion.g>
    </svg>
  );
}

// ============================================================================
// 5. ERROR ROBOT ILLUSTRATION (Robot d'alerte / Erreur)
// ============================================================================

export function ErrorRobotIllustration({ size = 160, className, style, ...props }: IllustrationProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      <motion.g animate={{ y: [-3, 3, -3], rotate: [-2, 2, -2] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }} style={{ transformOrigin: "80px 90px" }}>
        <line x1="80" y1="50" x2="80" y2="30" stroke="#1e293b" strokeWidth="2.5" className="dark:stroke-neutral-100" />
        <motion.circle cx="80" cy="24" r="6" fill="#f43f5e" animate={{ scale: [1, 1.3, 1], opacity: [0.4, 1, 0.4] }} transition={{ duration: 1, repeat: Infinity }} />
        <rect x="40" y="50" width="80" height="60" rx="14" fill="#ffffff" stroke="#1e293b" strokeWidth="3" className="dark:fill-neutral-900 dark:stroke-neutral-100" />
        <rect x="30" y="70" width="10" height="20" rx="3" fill="#94a3b8" />
        <rect x="120" y="70" width="10" height="20" rx="3" fill="#94a3b8" />
        <motion.g animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 1.5, repeat: Infinity }}>
          <line x1="58" y1="72" x2="68" y2="82" stroke="#f43f5e" strokeWidth="2.5" strokeLinecap="round" />
          <line x1="68" y1="72" x2="58" y2="82" stroke="#f43f5e" strokeWidth="2.5" strokeLinecap="round" />
          <line x1="92" y1="72" x2="102" y2="82" stroke="#f43f5e" strokeWidth="2.5" strokeLinecap="round" />
          <line x1="102" y1="72" x2="92" y2="82" stroke="#f43f5e" strokeWidth="2.5" strokeLinecap="round" />
        </motion.g>
        <path d="M60 98 Q 70 92, 80 98 T 100 98" stroke="#1e293b" strokeWidth="2.5" strokeLinecap="round" fill="none" className="dark:stroke-neutral-100" />
      </motion.g>
    </svg>
  );
}

// ============================================================================
// 6. USER ONBOARDING ILLUSTRATION (Refonte complète du visage & cheveux)
// ============================================================================

export function UserOnboardingIllustration({ size = 160, className, style, ...props }: IllustrationProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      <circle cx="80" cy="85" r="50" fill="#6366f1" fillOpacity="0.12" />
      {/* Bulle "Hello!" flottante */}
      <motion.g animate={{ y: [-4, 2, -4], scale: [0.95, 1.05, 0.95] }} transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}>
        <rect x="92" y="20" width="52" height="28" rx="8" fill="#3b82f6" />
        <polygon points="102,48 112,48 99,56" fill="#3b82f6" />
        <text x="118" y="38" fill="#ffffff" fontSize="11" fontWeight="bold" textAnchor="middle">Hello!</text>
      </motion.g>
      {/* Corps + visage + bras (tout solidaire) */}
      <g>
        {/* Corps - torse bleu */}
        <path d="M52 125c0-20 12-32 28-32s28 12 28 32v8H52v-8z" fill="#3b82f6" stroke="#1d4ed8" strokeWidth="2" />
        {/* Visage */}
        <circle cx="80" cy="60" r="20" fill="#fcd34d" stroke="#d97706" strokeWidth="2" />
        {/* Cheveux (calotte sur le dessus) */}
        <path d="M60 56 C60 42, 100 42, 100 56 C96 49, 64 49, 60 56 Z" fill="#1e293b" />
        {/* Yeux */}
        <circle cx="73" cy="60" r="2.3" fill="#0f172a" />
        <circle cx="87" cy="60" r="2.3" fill="#0f172a" />
        {/* Joues roses */}
        <circle cx="68" cy="65" r="3" fill="#f43f5e" opacity="0.35" />
        <circle cx="92" cy="65" r="3" fill="#f43f5e" opacity="0.35" />
        {/* Sourire */}
        <path d="M74 68 Q 80 74, 86 68" stroke="#0f172a" strokeWidth="2.2" strokeLinecap="round" fill="none" />
        {/* Bras droit animé — pivot ancré à l'épaule droite du corps (108, 93) */}
        <motion.g
          animate={{ rotate: [0, 28, -8, 28, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          style={{ transformOrigin: "108px 93px" }}
        >
          {/* Bras = ligne depuis l'épaule (108,93) vers la main (128,78) */}
          <line x1="108" y1="93" x2="128" y2="78" stroke="#fcd34d" strokeWidth="6" strokeLinecap="round" />
          {/* Main (petite paume ronde) */}
          <circle cx="128" cy="78" r="5" fill="#fcd34d" stroke="#d97706" strokeWidth="1.5" />
        </motion.g>
      </g>
    </svg>
  );
}

// ============================================================================
// 7. ANALYTICS CHART ILLUSTRATION (Personnage & Graphique de croissance)
// ============================================================================

export function AnalyticsChartIllustration({ size = 160, className, style, ...props }: IllustrationProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      <g>
        <motion.rect x="30" y="90" width="16" height="40" rx="3" fill="#10b981" opacity="0.4" animate={{ height: [30, 40, 30] }} transition={{ duration: 2, repeat: Infinity }} />
        <motion.rect x="52" y="70" width="16" height="60" rx="3" fill="#10b981" opacity="0.6" animate={{ height: [50, 60, 50] }} transition={{ duration: 2.2, repeat: Infinity, delay: 0.2 }} />
        <motion.rect x="74" y="50" width="16" height="80" rx="3" fill="#10b981" opacity="0.8" animate={{ height: [70, 80, 70] }} transition={{ duration: 2.4, repeat: Infinity, delay: 0.4 }} />
        <motion.rect x="96" y="30" width="16" height="100" rx="3" fill="#10b981" animate={{ height: [90, 100, 90] }} transition={{ duration: 2.6, repeat: Infinity, delay: 0.6 }} />
      </g>
      <motion.path d="M30 95L58 75L80 55L108 25" stroke="#34d399" strokeWidth="3" strokeLinecap="round" fill="none" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }} />
      <motion.g animate={{ y: [-3, 3, -3] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}>
        <rect x="110" y="15" width="42" height="22" rx="6" fill="#10b981" />
        <text x="131" y="30" fill="#ffffff" fontSize="10" fontWeight="bold" textAnchor="middle">+34%</text>
      </motion.g>
    </svg>
  );
}

// ============================================================================
// 8. PAYMENT CHECKOUT ILLUSTRATION (Carte bancaire & Pièce de monnaie)
// ============================================================================

export function PaymentCheckoutIllustration({ size = 160, className, style, ...props }: IllustrationProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      <motion.g animate={{ y: [-4, 4, -4], rotate: [-3, 3, -3] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}>
        <rect x="30" y="45" width="100" height="62" rx="10" fill="#4f46e5" stroke="#3730a3" strokeWidth="2" />
        <rect x="30" y="60" width="100" height="12" fill="#1e1b4b" />
        <rect x="42" y="80" width="16" height="12" rx="2" fill="#fbbf24" />
        <circle cx="110" cy="86" r="7" fill="#f43f5e" />
        <circle cx="118" cy="86" r="7" fill="#fbbf24" opacity="0.8" />
      </motion.g>
      <motion.g animate={{ y: [15, 45, 15], opacity: [1, 0.8, 1] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
        <circle cx="80" cy="15" r="12" fill="#fbbf24" stroke="#d97706" strokeWidth="2" />
        <text x="80" y="19" fill="#78350f" fontSize="12" fontWeight="bold" textAnchor="middle">€</text>
      </motion.g>
    </svg>
  );
}

// ============================================================================
// 9. SYSTEM MAINTENANCE ILLUSTRATION (Robot Technicien & Engrenage)
// ============================================================================

export function SystemMaintenanceIllustration({ size = 160, className, style, ...props }: IllustrationProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      <motion.g animate={{ rotate: [0, 360] }} transition={{ duration: 12, repeat: Infinity, ease: "linear" }} style={{ transformOrigin: "80px 80px" }}>
        <circle cx="80" cy="80" r="35" fill="none" stroke="#6366f1" strokeWidth="6" strokeDasharray="8 6" />
        <circle cx="80" cy="80" r="18" fill="#ffffff" stroke="#1e293b" strokeWidth="3" className="dark:fill-neutral-900 dark:stroke-neutral-100" />
      </motion.g>
      <motion.g animate={{ rotate: [-20, 20, -20] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }} style={{ transformOrigin: "110px 50px" }}>
        <path d="M110 50L135 25c4-4 10-4 14 0s4 10 0 14L124 64" stroke="#f59e0b" strokeWidth="4" strokeLinecap="round" fill="none" />
      </motion.g>
    </svg>
  );
}

// ============================================================================
// 10. NOT FOUND 404 ILLUSTRATION (Astronaute & Planète)
// ============================================================================

export function NotFoundIllustration({ size = 160, className, style, ...props }: IllustrationProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      <circle cx="80" cy="80" r="42" fill="#9333ea" fillOpacity="0.18" stroke="#9333ea" strokeWidth="2.5" />
      <ellipse cx="80" cy="80" rx="64" ry="14" fill="none" stroke="#3b82f6" strokeWidth="2.5" strokeDasharray="5 3" />
      <text x="80" y="88" fill="#1e293b" className="font-mono font-bold dark:fill-purple-100" fontSize="24" textAnchor="middle">404</text>
      <motion.g animate={{ y: [-8, 8, -8], rotate: [-10, 10, -10] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }} style={{ transformOrigin: "40px 40px" }}>
        <circle cx="40" cy="40" r="13" fill="#ffffff" stroke="#1e293b" strokeWidth="2.5" className="dark:fill-neutral-900 dark:stroke-neutral-100" />
        <rect x="33" y="36" width="14" height="8" rx="3" fill="#38bdf8" />
      </motion.g>
    </svg>
  );
}

// ============================================================================
// 11. SEARCH NO RESULTS ILLUSTRATION (Loupe d'exploration & Empreintes)
// ============================================================================

export function SearchNoResultsIllustration({ size = 160, className, style, ...props }: IllustrationProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      <motion.g animate={{ x: [-10, 10, -10], y: [-5, 5, -5], rotate: [-5, 5, -5] }} transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}>
        <circle cx="75" cy="70" r="32" fill="#ffffff" stroke="#1e293b" strokeWidth="4" className="dark:fill-neutral-900 dark:stroke-neutral-100" />
        <line x1="98" y1="93" x2="130" y2="125" stroke="#64748b" strokeWidth="6" strokeLinecap="round" />
        <text x="75" y="79" fill="#f59e0b" className="font-mono font-bold" fontSize="26" textAnchor="middle">?</text>
      </motion.g>
    </svg>
  );
}

// ============================================================================
// 12. ROCKET LAUNCH ILLUSTRATION (Fusée au décollage & Propulsion)
// ============================================================================

export function RocketLaunchIllustration({ size = 160, className, style, ...props }: IllustrationProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      <motion.circle cx="65" cy="135" r="14" fill="#cbd5e1" opacity="0.6" animate={{ scale: [0.9, 1.2, 0.9] }} transition={{ duration: 1.5, repeat: Infinity }} />
      <motion.circle cx="95" cy="135" r="14" fill="#cbd5e1" opacity="0.6" animate={{ scale: [1.2, 0.9, 1.2] }} transition={{ duration: 1.5, repeat: Infinity }} />
      <motion.g animate={{ y: [-5, 5, -5] }} transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}>
        <motion.polygon points="73,110 87,110 80,135" fill="#f97316" animate={{ scaleY: [0.8, 1.3, 0.8] }} transition={{ duration: 0.3, repeat: Infinity }} style={{ transformOrigin: "80px 110px" }} />
        <motion.polygon points="76,110 84,110 80,125" fill="#fbbf24" animate={{ scaleY: [1.2, 0.7, 1.2] }} transition={{ duration: 0.3, repeat: Infinity }} style={{ transformOrigin: "80px 110px" }} />
        <path d="M80 25 C65 50, 65 95, 65 95 L95 95 C95 95, 95 50, 80 25 Z" fill="#ffffff" stroke="#1e293b" strokeWidth="3" className="dark:fill-neutral-900 dark:stroke-neutral-100" />
        <path d="M65 80 L48 98 L65 95 Z" fill="#f43f5e" />
        <path d="M95 80 L112 98 L95 95 Z" fill="#f43f5e" />
        <circle cx="80" cy="60" r="8" fill="#38bdf8" stroke="#1e293b" strokeWidth="2.5" />
      </motion.g>
    </svg>
  );
}

// ============================================================================
// 13. SUCCESS CHECK ILLUSTRATION (Sceau de validation & Ondes d'énergie)
// ============================================================================

export function SuccessCheckIllustration({ size = 160, className, style, ...props }: IllustrationProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      <motion.circle cx="80" cy="80" r="55" stroke="#10b981" strokeWidth="2" strokeOpacity="0.3" fill="none" animate={{ scale: [0.9, 1.15, 0.9], opacity: [0.2, 0.6, 0.2] }} transition={{ duration: 2.4, repeat: Infinity }} />
      <motion.circle cx="80" cy="80" r="42" fill="#10b981" stroke="#059669" strokeWidth="3" animate={{ scale: [0.98, 1.04, 0.98] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }} />
      <motion.path d="M62 80 L74 92 L98 68" stroke="#ffffff" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.2, repeat: Infinity, repeatDelay: 1 }} />
    </svg>
  );
}

// ============================================================================
// 14. SHIELD LOCK ILLUSTRATION (Coffre-Fort & Clé Numérique)
// ============================================================================

export function ShieldLockIllustration({ size = 160, className, style, ...props }: IllustrationProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      <path d="M80 25 L125 42 V80 C125 110, 80 135, 80 135 C80 135, 35 110, 35 80 V42 Z" fill="#6366f1" fillOpacity="0.15" stroke="#6366f1" strokeWidth="3" />
      <motion.g animate={{ y: [-4, 4, -4] }} transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}>
        <rect x="58" y="65" width="44" height="35" rx="6" fill="#6366f1" stroke="#4f46e5" strokeWidth="2" />
        <path d="M66 65 V52 C66 44, 94 44, 94 52 V65" fill="none" stroke="#6366f1" strokeWidth="4" strokeLinecap="round" />
        <circle cx="80" cy="80" r="4" fill="#ffffff" />
      </motion.g>
    </svg>
  );
}

// ============================================================================
// 15. INBOX MAIL ILLUSTRATION (REFONTE ULTRA-ANIMÉE - Boîte de Réception SOTA)
// ============================================================================

export function InboxMailIllustration({ size = 160, className, style, ...props }: IllustrationProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      {/* Ombre portée dynamique */}
      <ellipse cx="80" cy="142" rx="42" ry="7" fill="#64748b" fillOpacity="0.18" />

      {/* Enveloppe (fond) puis lettre par dessus */}
      <motion.g animate={{ y: [-3, 3, -3] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}>
        {/* 1. Corps de l'enveloppe (dessiné en premier = derrière) */}
        <rect x="30" y="75" width="100" height="65" rx="10" fill="#dbeafe" stroke="#3b82f6" strokeWidth="2.5" className="dark:fill-slate-800" />
        {/* Rabat inférieur (pli central) */}
        <path d="M30 78 L80 110 L130 78" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />

        {/* 2. Lettre qui lévite hors de l'enveloppe (dessinée après = par dessus) */}
        <motion.g animate={{ y: [-18, 0, -18] }} transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}>
          <rect x="42" y="42" width="76" height="52" rx="6" fill="#ffffff" stroke="#3b82f6" strokeWidth="2" className="dark:fill-neutral-800" />
          <line x1="54" y1="56" x2="104" y2="56" stroke="#3b82f6" strokeWidth="3" strokeLinecap="round" />
          <line x1="54" y1="66" x2="90" y2="66" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" />
          <line x1="54" y1="76" x2="100" y2="76" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" />
        </motion.g>

        {/* Pastille de notification rouge "+1" pulsante */}
        <motion.g animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 1.5, repeat: Infinity }} style={{ transformOrigin: "130px 72px" }}>
          <circle cx="130" cy="72" r="13" fill="#f43f5e" />
          <text x="130" y="77" fill="#ffffff" fontSize="12" fontWeight="bold" textAnchor="middle">+1</text>
        </motion.g>
      </motion.g>

      {/* Petit avion en papier compact (haut-gauche, chemin lisible) */}
      <motion.g
        animate={{ x: [-6, 6, -6], y: [-5, 5, -5], rotate: [-6, 6, -6] }}
        transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
      >
        {/* Corps de l'avion: triangle fin + triangle de l'aile repliée */}
        <polygon points="20,30 52,42 36,50" fill="#3b82f6" strokeLinejoin="round" />
        <polygon points="36,50 52,42 44,58" fill="#1d4ed8" strokeLinejoin="round" />
      </motion.g>
    </svg>
  );
}

// ============================================================================
// 16. EMAIL SENT ILLUSTRATION (Envoi réussi & Sceau Vert Volant)
// ============================================================================

export function EmailSentIllustration({ size = 160, className, style, ...props }: IllustrationProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      {/* Halo de succès derrière */}
      <motion.circle cx="80" cy="82" r="52" fill="#10b981" fillOpacity="0.07" animate={{ scale: [0.92, 1.08, 0.92] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }} />

      {/* Enveloppe flottante — tout est dans un seul groupe animé */}
      <motion.g animate={{ y: [-5, 5, -5] }} transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}>
        {/* Corps de l'enveloppe */}
        <rect x="28" y="55" width="104" height="72" rx="10" fill="#10b981" fillOpacity="0.12" stroke="#10b981" strokeWidth="2.5" />
        {/* Rabat fermé en V (commence et finit exactement aux coins supérieurs du rect) */}
        <path d="M28 55 L80 98 L132 55" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        {/* Diagonales de fermeture du bas de l'enveloppe */}
        <line x1="28" y1="127" x2="66" y2="99" stroke="#10b981" strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
        <line x1="132" y1="127" x2="94" y2="99" stroke="#10b981" strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
      </motion.g>

      {/* Badge de succès vert — flotte indépendamment, légèrement décalé */}
      <motion.g animate={{ y: [-7, 3, -7], scale: [0.97, 1.03, 0.97] }} transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}>
        <circle cx="116" cy="50" r="18" fill="#10b981" stroke="#ffffff" strokeWidth="3" className="dark:stroke-neutral-900" />
        <path d="M107 50 L113 56 L125 42" stroke="#ffffff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </motion.g>

      {/* Étoile décorative gauche */}
      <motion.path
        d="M24 48l2 4 4 2-4 2-2 4-2-4-4-2 4-2z"
        fill="#10b981"
        animate={{ scale: [0.6, 1.2, 0.6], opacity: [0.3, 1, 0.3] }}
        transition={{ duration: 2.4, repeat: Infinity }}
        style={{ transformOrigin: "24px 48px" }}
      />
    </svg>
  );
}

// ============================================================================
// 17. CHAT MESSAGE ILLUSTRATION (Discussion / Bulles de Chat & Points '...')
// ============================================================================

export function ChatMessageIllustration({ size = 160, className, style, ...props }: IllustrationProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      {/* Bulle de gauche (Reçue) */}
      <motion.g animate={{ y: [-3, 3, -3] }} transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}>
        <rect x="25" y="40" width="75" height="45" rx="12" fill="#ffffff" stroke="#1e293b" strokeWidth="2.5" className="dark:fill-neutral-900 dark:stroke-neutral-100" />
        <polygon points="35,85 45,85 30,95" fill="#ffffff" stroke="#1e293b" strokeWidth="2.5" className="dark:fill-neutral-900 dark:stroke-neutral-100" />
        {/* Lignes de texte simulées */}
        <line x1="40" y1="55" x2="80" y2="55" stroke="#94a3b8" strokeWidth="3" strokeLinecap="round" />
        <line x1="40" y1="67" x2="68" y2="67" stroke="#94a3b8" strokeWidth="3" strokeLinecap="round" />
      </motion.g>

      {/* Bulle de droite (Envoyée avec 3 points animés '...') */}
      <motion.g animate={{ y: [3, -3, 3] }} transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}>
        {/*
          Bulle bleue avec queue en bas-DROITE:
          On utilise un path pour créer le rectangle arrondi + la queue dans un seul shape propre.
          La queue part du coin bas-droit du rectangle.
        */}
        <path
          d="M77 85 h46 a12 12 0 0 1 12 12 v18 a12 12 0 0 1 -12 12 h-46 a12 12 0 0 1 -12 -12 v-18 a12 12 0 0 1 12 -12 z"
          fill="#3b82f6"
        />
        {/* Queue: triangle au coin bas-droit */}
        <polygon points="122,127 135,127 135,142" fill="#3b82f6" />

        {/* 3 petits points de frappe animés en vague */}
        <motion.circle cx="85" cy="106" r="3.5" fill="#ffffff" animate={{ y: [0, -4, 0] }} transition={{ duration: 0.8, repeat: Infinity }} />
        <motion.circle cx="100" cy="106" r="3.5" fill="#ffffff" animate={{ y: [0, -4, 0] }} transition={{ duration: 0.8, repeat: Infinity, delay: 0.2 }} />
        <motion.circle cx="115" cy="106" r="3.5" fill="#ffffff" animate={{ y: [0, -4, 0] }} transition={{ duration: 0.8, repeat: Infinity, delay: 0.4 }} />
      </motion.g>
    </svg>
  );
}

// ============================================================================
// 18. NOTIFICATION CENTER ILLUSTRATION (Cloche & Cartes d'Alerte Flottantes)
// ============================================================================

export function NotificationCenterIllustration({ size = 160, className, style, ...props }: IllustrationProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      {/* 1. Carte de notification (dessinée EN PREMIER = derrière) */}
      <motion.g animate={{ x: [-4, 4, -4], y: [2, 5, 2] }} transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}>
        <rect x="15" y="98" width="130" height="50" rx="12" fill="#ffffff" stroke="#e2e8f0" strokeWidth="2" className="dark:fill-neutral-900 dark:stroke-neutral-700" />
        {/* Icône d'app bleue */}
        <rect x="26" y="109" width="22" height="22" rx="6" fill="#3b82f6" />
        <path d="M32 120 L36 124 L43 116" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        {/* Texte de la notif */}
        <rect x="56" y="111" width="72" height="6" rx="3" fill="#1e293b" opacity="0.8" className="dark:fill-white" />
        <rect x="56" y="123" width="50" height="5" rx="2.5" fill="#94a3b8" />
        {/* Timestamp */}
        <text x="128" y="116" fill="#94a3b8" fontSize="7" textAnchor="end">2m</text>
      </motion.g>

      {/* 2. Cloche oscillante par dessus la carte (dessinée EN SECOND) */}
      <motion.g
        animate={{ rotate: [-10, 10, -10] }}
        transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
        style={{ transformOrigin: "80px 30px" }}
      >
        {/* Tige de suspension */}
        <line x1="80" y1="20" x2="80" y2="32" stroke="#f59e0b" strokeWidth="3" strokeLinecap="round" />
        {/* Corps de la cloche */}
        <path d="M58 78 C58 56 67 32 80 32 C93 32 102 56 102 78 C102 84 56 84 58 78 Z" fill="#fef3c7" stroke="#f59e0b" strokeWidth="3" />
        {/* Battant de la cloche */}
        <circle cx="80" cy="86" r="5" fill="#f59e0b" />
        {/* Badge de comptage — ancré par rapport à la cloche, pas à transformOrigin */}
        <circle cx="102" cy="32" r="10" fill="#f43f5e" stroke="#ffffff" strokeWidth="2" />
        <text x="102" y="36" fill="#ffffff" fontSize="9" fontWeight="bold" textAnchor="middle">3</text>
      </motion.g>
    </svg>
  );
}

// ============================================================================
// 19. NEWSLETTER SUBSCRIBE ILLUSTRATION (Boîte aux Lettres & Drapeaux)
// ============================================================================

export function NewsletterSubscribeIllustration({ size = 160, className, style, ...props }: IllustrationProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      {/* Poteau au sol */}
      <rect x="76" y="105" width="8" height="40" fill="#64748b" />

      {/* Boîte aux lettres américaine */}
      <path d="M45 60 C45 40, 115 40, 115 60 V105 H45 Z" fill="#ffffff" stroke="#1e293b" strokeWidth="3" className="dark:fill-neutral-900 dark:stroke-neutral-100" />
      <path d="M45 60 C45 40, 75 40, 75 60 V105 H45 Z" fill="#3b82f6" fillOpacity="0.2" stroke="#1e293b" strokeWidth="2" />

      {/* Drapeau rouge d'alerte levé */}
      <motion.g animate={{ rotate: [-5, 5, -5] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }} style={{ transformOrigin: "115px 75px" }}>
        <rect x="115" y="50" width="4" height="25" fill="#f43f5e" />
        <rect x="119" y="50" width="16" height="10" rx="1" fill="#f43f5e" />
      </motion.g>

      {/* Enveloppe insérée à l'intérieur */}
      <motion.g animate={{ y: [-6, 2, -6] }} transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}>
        <rect x="52" y="48" width="56" height="34" rx="4" fill="#ffffff" stroke="#3b82f6" strokeWidth="2" className="dark:fill-neutral-800" />
        <path d="M52 50 L80 70 L108 50" stroke="#3b82f6" strokeWidth="1.75" fill="none" />
      </motion.g>
    </svg>
  );
}

// ============================================================================
// SCÈNES À VARIABLES — Props interactives pour des états dynamiques
// ============================================================================

// ============================================================================
// 20. LOADING PROGRESS ILLUSTRATION (progress: 0-100, status)
// ============================================================================

export interface LoadingProgressProps extends IllustrationProps {
  /** Pourcentage de complétion, de 0 à 100 */
  progress?: number;
  /** État du chargement */
  status?: "loading" | "processing" | "done";
  /** Label affiché sous la barre (max 28 caractères) */
  label?: string;
}

export function LoadingProgressIllustration({
  size = 160,
  progress = 60,
  status = "loading",
  label,
  className,
  style,
  ...props
}: LoadingProgressProps) {
  const pct = Math.min(100, Math.max(0, progress));
  const isDone = status === "done" || pct === 100;
  const color = isDone ? "#10b981" : status === "processing" ? "#f59e0b" : "#6366f1";
  const trackW = 100; // largeur piste
  const barW = (pct / 100) * trackW;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      {/* Spinner ou checkmark selon état */}
      {isDone ? (
        <motion.g initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 300 }}>
          <circle cx="80" cy="42" r="22" fill="#10b981" />
          <path d="M70 42 L77 49 L92 33" stroke="#ffffff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </motion.g>
      ) : (
        <motion.g animate={{ rotate: [0, 360] }} transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }} style={{ transformOrigin: "80px 42px" }}>
          <circle cx="80" cy="42" r="20" fill="none" stroke={color} strokeOpacity="0.2" strokeWidth="5" />
          <path d="M80 22 A20 20 0 0 1 100 42" stroke={color} strokeWidth="5" strokeLinecap="round" fill="none" />
        </motion.g>
      )}

      {/* Piste de barre */}
      <rect x="30" y="80" width={trackW} height="14" rx="7" fill="#e2e8f0" />
      {/* Remplissage animé */}
      <motion.rect
        x="30"
        y="80"
        height="14"
        rx="7"
        fill={color}
        animate={{ width: barW }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      />
      {/* Extrémité lumineuse */}
      {barW > 8 && (
        <motion.circle cx={30 + barW} cy="87" r="5" fill="#ffffff" opacity="0.5" animate={{ opacity: [0.3, 0.8, 0.3] }} transition={{ duration: 1, repeat: Infinity }} />
      )}

      {/* Pourcentage */}
      <text x="80" y="114" fill="#1e293b" fontSize="18" fontWeight="bold" textAnchor="middle" className="dark:fill-white">{pct}%</text>

      {/* Label optionnel */}
      {label && (
        <text x="80" y="130" fill="#64748b" fontSize="9" textAnchor="middle">{label.slice(0, 28)}</text>
      )}

      {/* Points pulsants si pas done */}
      {!isDone && (
        <g>
          <motion.circle cx="65" cy="148" r="4" fill={color} animate={{ y: [0, -6, 0] }} transition={{ duration: 0.7, repeat: Infinity }} />
          <motion.circle cx="80" cy="148" r="4" fill={color} animate={{ y: [0, -6, 0] }} transition={{ duration: 0.7, repeat: Infinity, delay: 0.15 }} />
          <motion.circle cx="95" cy="148" r="4" fill={color} animate={{ y: [0, -6, 0] }} transition={{ duration: 0.7, repeat: Infinity, delay: 0.3 }} />
        </g>
      )}
    </svg>
  );
}

// ============================================================================
// 21. FILE UPLOAD ILLUSTRATION (progress, status, fileName)
// ============================================================================

export interface FileUploadProps extends IllustrationProps {
  /** Pourcentage d'upload, de 0 à 100 */
  progress?: number;
  /** État de l'upload */
  status?: "idle" | "uploading" | "done" | "error";
  /** Nom du fichier (max 18 car.) */
  fileName?: string;
}

export function FileUploadIllustration({
  size = 160,
  progress = 0,
  status = "idle",
  fileName = "document.pdf",
  className,
  style,
  ...props
}: FileUploadProps) {
  const pct = Math.min(100, Math.max(0, progress));
  const barW = (pct / 100) * 84;
  const statusColor = status === "done" ? "#10b981" : status === "error" ? "#f43f5e" : status === "uploading" ? "#3b82f6" : "#64748b";

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      {/* Icône de fichier */}
      <motion.g animate={{ y: status === "uploading" ? [-4, 4, -4] : 0 }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}>
        <rect x="48" y="20" width="64" height="78" rx="8" fill="#ffffff" stroke="#e2e8f0" strokeWidth="2.5" className="dark:fill-neutral-900 dark:stroke-neutral-700" />
        {/* Coin plié en haut-droite */}
        <path d="M96 20 L112 36 L96 36 Z" fill="#e2e8f0" className="dark:fill-neutral-700" />
        <line x1="96" y1="20" x2="96" y2="36" stroke="#e2e8f0" strokeWidth="1.5" className="dark:stroke-neutral-700" />

        {/* Icône central selon statut */}
        {status === "idle" && (
          <>
            <path d="M80 55 L80 75" stroke="#64748b" strokeWidth="3" strokeLinecap="round" />
            <path d="M72 63 L80 55 L88 63" stroke="#64748b" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </>
        )}
        {status === "uploading" && (
          <motion.g animate={{ y: [-3, 0, -3] }} transition={{ duration: 0.8, repeat: Infinity }}>
            <path d="M80 55 L80 75" stroke="#3b82f6" strokeWidth="3" strokeLinecap="round" />
            <path d="M72 62 L80 54 L88 62" stroke="#3b82f6" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </motion.g>
        )}
        {status === "done" && (
          <motion.g initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring" }}>
            <circle cx="80" cy="63" r="14" fill="#10b981" fillOpacity="0.15" />
            <path d="M73 63 L78 68 L87 57" stroke="#10b981" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </motion.g>
        )}
        {status === "error" && (
          <motion.g animate={{ scale: [1, 1.08, 1] }} transition={{ duration: 1.2, repeat: Infinity }}>
            <circle cx="80" cy="63" r="14" fill="#f43f5e" fillOpacity="0.12" />
            <line x1="74" y1="57" x2="86" y2="69" stroke="#f43f5e" strokeWidth="3.5" strokeLinecap="round" />
            <line x1="86" y1="57" x2="74" y2="69" stroke="#f43f5e" strokeWidth="3.5" strokeLinecap="round" />
          </motion.g>
        )}

        {/* Nom de fichier */}
        <text x="80" y="87" fill="#1e293b" fontSize="8" textAnchor="middle" className="dark:fill-white">{fileName.slice(0, 18)}</text>
      </motion.g>

      {/* Barre de progression */}
      <rect x="38" y="112" width="84" height="8" rx="4" fill="#e2e8f0" />
      <motion.rect x="38" y="112" height="8" rx="4" fill={statusColor} animate={{ width: barW }} transition={{ duration: 0.5, ease: "easeOut" }} />

      {/* % et statut */}
      <text x="80" y="135" fill={statusColor} fontSize="10" fontWeight="bold" textAnchor="middle">
        {status === "done" ? "✓ Envoyé" : status === "error" ? "✕ Erreur" : status === "uploading" ? `${pct}%` : "Prêt"}
      </text>
    </svg>
  );
}

// ============================================================================
// 22. PASSWORD STRENGTH ILLUSTRATION (strength: 0-4)
// ============================================================================

export interface PasswordStrengthProps extends IllustrationProps {
  /** Force du mot de passe: 0=vide, 1=faible, 2=moyen, 3=bon, 4=excellent */
  strength?: 0 | 1 | 2 | 3 | 4;
}

const STRENGTH_LABELS = ["Vide", "Faible", "Moyen", "Bon", "Excellent"];
const STRENGTH_COLORS = ["#e2e8f0", "#f43f5e", "#f97316", "#f59e0b", "#10b981"];

export function PasswordStrengthIllustration({
  size = 160,
  strength = 0,
  className,
  style,
  ...props
}: PasswordStrengthProps) {
  const color = STRENGTH_COLORS[strength] ?? "#e2e8f0";
  const label = STRENGTH_LABELS[strength] ?? "Vide";

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      {/* Cadenas animé */}
      <motion.g animate={{ y: [-3, 3, -3] }} transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}>
        {/* Anse */}
        <path
          d={strength >= 3 ? "M60 68 V54 C60 40 100 40 100 54 V68" : "M60 68 V54 C60 40 100 40 100 54"}
          fill="none"
          stroke={color}
          strokeWidth="5"
          strokeLinecap="round"
          style={{ transition: "stroke 0.4s" }}
        />
        {/* Corps */}
        <rect x="48" y="68" width="64" height="50" rx="10" fill={color} fillOpacity="0.18" stroke={color} strokeWidth="3" style={{ transition: "stroke 0.4s, fill 0.4s" }} />
        {/* Trou de serrure */}
        <circle cx="80" cy="90" r="7" fill={color} fillOpacity="0.5" style={{ transition: "fill 0.4s" }} />
        <rect x="77" y="90" width="6" height="10" rx="3" fill={color} style={{ transition: "fill 0.4s" }} />
      </motion.g>

      {/* 4 barres de force */}
      <g>
        {[0, 1, 2, 3].map((i) => (
          <motion.rect
            key={i}
            x={24 + i * 28}
            y="132"
            width="20"
            height="8"
            rx="4"
            fill={i < strength ? STRENGTH_COLORS[strength] : "#e2e8f0"}
            animate={{ scaleY: i < strength ? 1 : 0.6, opacity: i < strength ? 1 : 0.4 }}
            transition={{ duration: 0.3, delay: i * 0.05 }}
            style={{ transformOrigin: `${24 + i * 28 + 10}px 136px` }}
          />
        ))}
      </g>

      {/* Label de force */}
      <text x="80" y="155" fill={color} fontSize="10" fontWeight="bold" textAnchor="middle" style={{ transition: "fill 0.4s" }}>{label}</text>
    </svg>
  );
}

// ============================================================================
// 23. STORAGE METER ILLUSTRATION (used: 0-100)
// ============================================================================

export interface StorageMeterProps extends IllustrationProps {
  /** Pourcentage du stockage utilisé, de 0 à 100 */
  used?: number;
  /** Légende (ex: "4.2 Go / 10 Go") */
  label?: string;
}

export function StorageMeterIllustration({
  size = 160,
  used = 65,
  label,
  className,
  style,
  ...props
}: StorageMeterProps) {
  const pct = Math.min(100, Math.max(0, used));
  const color = pct >= 90 ? "#f43f5e" : pct >= 70 ? "#f59e0b" : "#6366f1";
  // Arc SVG: cercle r=50, circumference = 2πr ≈ 314. On utilise un arc de 75% (270°)
  const radius = 50;
  const circ = 2 * Math.PI * radius;
  const trackDash = circ * 0.78; // 78% du cercle comme track
  const fillDash = trackDash * (pct / 100);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      {/* Piste du donut (gris) */}
      <circle
        cx="80"
        cy="80"
        r={radius}
        fill="none"
        stroke="#e2e8f0"
        strokeWidth="14"
        strokeDasharray={`${trackDash} ${circ}`}
        strokeDashoffset={`${-circ * 0.11}`}
        strokeLinecap="round"
        transform="rotate(-234 80 80)"
      />
      {/* Remplissage du donut (coloré) */}
      <motion.circle
        cx="80"
        cy="80"
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth="14"
        strokeLinecap="round"
        strokeDasharray={`${fillDash} ${circ}`}
        strokeDashoffset={`${-circ * 0.11}`}
        transform="rotate(-234 80 80)"
        animate={{ strokeDasharray: `${fillDash} ${circ}`, stroke: color }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      />
      {/* Icône disque dur au centre */}
      <rect x="66" y="66" width="28" height="20" rx="4" fill={color} fillOpacity="0.15" stroke={color} strokeWidth="2" />
      <circle cx="80" cy="76" r="4" fill={color} fillOpacity="0.6" />

      {/* Pourcentage */}
      <text x="80" y="100" fill="#1e293b" fontSize="13" fontWeight="bold" textAnchor="middle" className="dark:fill-white">{pct}%</text>

      {/* Légende */}
      {label && (
        <text x="80" y="148" fill="#64748b" fontSize="9" textAnchor="middle">{label.slice(0, 22)}</text>
      )}
      {!label && (
        <text x="80" y="148" fill="#64748b" fontSize="9" textAnchor="middle">stockage utilisé</text>
      )}
    </svg>
  );
}

// ============================================================================
// 24. TEAM AVATARS ILLUSTRATION (count: 1-6)
// ============================================================================

export interface TeamAvatarsProps extends IllustrationProps {
  /** Nombre de membres à afficher (1 à 6) */
  count?: number;
}

const AVATAR_COLORS = ["#6366f1", "#3b82f6", "#10b981", "#f59e0b", "#f43f5e", "#8b5cf6"];
const AVATAR_INITIALS = ["A", "B", "C", "D", "E", "F"];

export function TeamAvatarsIllustration({
  size = 160,
  count = 3,
  className,
  style,
  ...props
}: TeamAvatarsProps) {
  const n = Math.min(6, Math.max(1, count));
  const avatarR = 22;
  const spacing = 30;
  const totalW = (n - 1) * spacing + avatarR * 2;
  const startX = 80 - totalW / 2 + avatarR;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      {/* Avatars empilés */}
      {Array.from({ length: n }).map((_, i) => {
        const cx = startX + i * spacing;
        const color = AVATAR_COLORS[i % AVATAR_COLORS.length];
        return (
          <motion.g
            key={i}
            animate={{ y: [-3, 3, -3] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: i * 0.15 }}
          >
            {/* Contour blanc (séparation entre avatars) */}
            <circle cx={cx} cy="72" r={avatarR + 3} fill="#ffffff" className="dark:fill-neutral-950" />
            {/* Avatar */}
            <circle cx={cx} cy="72" r={avatarR} fill={color} />
            <text x={cx} y="77" fill="#ffffff" fontSize="13" fontWeight="bold" textAnchor="middle">
              {AVATAR_INITIALS[i]}
            </text>
          </motion.g>
        );
      })}

      {/* Étiquette */}
      <motion.g animate={{ scale: [0.95, 1.05, 0.95] }} transition={{ duration: 2.5, repeat: Infinity }}>
        <rect x="50" y="108" width="60" height="22" rx="11" fill={AVATAR_COLORS[0]} />
        <text x="80" y="123" fill="#ffffff" fontSize="10" fontWeight="bold" textAnchor="middle">
          {n} membre{n > 1 ? "s" : ""}
        </text>
      </motion.g>
    </svg>
  );
}

// ============================================================================
// 25. NETWORK STATUS ILLUSTRATION (status: connected | connecting | offline)
// ============================================================================

export interface NetworkStatusProps extends IllustrationProps {
  /** État de la connexion réseau */
  status?: "connected" | "connecting" | "offline";
}

export function NetworkStatusIllustration({
  size = 160,
  status = "connected",
  className,
  style,
  ...props
}: NetworkStatusProps) {
  const isOffline = status === "offline";
  const isConnecting = status === "connecting";
  const color = isOffline ? "#f43f5e" : isConnecting ? "#f59e0b" : "#10b981";

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      {/* Arcs WiFi — 3 arcs concentriques */}
      {/* Point central */}
      <motion.circle
        cx="80"
        cy="100"
        r="7"
        fill={color}
        animate={{ scale: isConnecting ? [1, 1.3, 1] : 1, opacity: isOffline ? 0.4 : 1 }}
        transition={{ duration: 0.8, repeat: Infinity }}
        style={{ transition: "fill 0.4s" }}
      />

      {/* Arc petit */}
      {!isOffline && (
        <motion.path
          d="M65 90 A20 20 0 0 1 95 90"
          stroke={color}
          strokeWidth="5"
          strokeLinecap="round"
          fill="none"
          animate={{ opacity: isConnecting ? [0.3, 1, 0.3] : 1 }}
          transition={{ duration: 0.8, repeat: Infinity, delay: 0.2 }}
        />
      )}

      {/* Arc moyen */}
      {!isOffline && (
        <motion.path
          d="M52 75 A38 38 0 0 1 108 75"
          stroke={color}
          strokeWidth="5"
          strokeLinecap="round"
          fill="none"
          animate={{ opacity: isConnecting ? [0.3, 1, 0.3] : 1 }}
          transition={{ duration: 0.8, repeat: Infinity, delay: 0.4 }}
        />
      )}

      {/* Grand arc */}
      {!isOffline && (
        <motion.path
          d="M38 60 A56 56 0 0 1 122 60"
          stroke={color}
          strokeWidth="5"
          strokeLinecap="round"
          fill="none"
          animate={{ opacity: isConnecting ? [0.3, 1, 0.3] : 1 }}
          transition={{ duration: 0.8, repeat: Infinity, delay: 0.6 }}
        />
      )}

      {/* Si offline: croix rouge */}
      {isOffline && (
        <motion.g animate={{ scale: [1, 1.06, 1] }} transition={{ duration: 1.5, repeat: Infinity }}>
          <circle cx="80" cy="75" r="28" fill="#f43f5e" fillOpacity="0.1" />
          <line x1="66" y1="61" x2="94" y2="89" stroke="#f43f5e" strokeWidth="5" strokeLinecap="round" />
          <line x1="94" y1="61" x2="66" y2="89" stroke="#f43f5e" strokeWidth="5" strokeLinecap="round" />
        </motion.g>
      )}

      {/* Label d'état */}
      <rect x="44" y="118" width="72" height="22" rx="11" fill={color} fillOpacity="0.12" stroke={color} strokeWidth="1.5" />
      <text x="80" y="133" fill={color} fontSize="10" fontWeight="bold" textAnchor="middle" style={{ transition: "fill 0.4s" }}>
        {isOffline ? "Hors ligne" : isConnecting ? "Connexion…" : "Connecté"}
      </text>
    </svg>
  );
}

// ============================================================================
// 26. WEATHER ILLUSTRATION
// ============================================================================

export interface WeatherProps extends IllustrationProps {
  condition?: "sunny" | "cloudy" | "rainy" | "stormy" | "snowy";
}

export function WeatherIllustration({ size = 160, condition = "sunny", className, style, ...props }: WeatherProps) {
  const isSunny = condition === "sunny";
  const isCloudy = condition === "cloudy";
  const isRainy = condition === "rainy";
  const isStormy = condition === "stormy";
  const isSnowy = condition === "snowy";
  const hasCloud = !isSunny;
  const sunCx = isCloudy ? 55 : 80;
  const sunCy = isCloudy ? 55 : 72;
  const cloudColor = isStormy ? "#334155" : "#dde6f0";

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      {(isSunny || isCloudy) && (
        <>
          <motion.g animate={{ rotate: [0, 360] }} transition={{ duration: 12, repeat: Infinity, ease: "linear" }} style={{ transformOrigin: `${sunCx}px ${sunCy}px` }}>
            {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => {
              const r = isCloudy ? 22 : 30;
              const rad = (a * Math.PI) / 180;
              return <line key={a} x1={sunCx + Math.cos(rad) * (r + 2)} y1={sunCy + Math.sin(rad) * (r + 2)} x2={sunCx + Math.cos(rad) * (r + 9)} y2={sunCy + Math.sin(rad) * (r + 9)} stroke="#fbbf24" strokeWidth="3.5" strokeLinecap="round" />;
            })}
          </motion.g>
          <motion.circle cx={sunCx} cy={sunCy} r={isCloudy ? 19 : 27} fill="#fbbf24" animate={{ scale: [0.95, 1.05, 0.95] }} transition={{ duration: 2.5, repeat: Infinity }} />
        </>
      )}
      {hasCloud && (
        <motion.g animate={{ x: [-3, 3, -3] }} transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}>
          <ellipse cx="82" cy="70" rx="38" ry="22" fill={cloudColor} />
          <ellipse cx="58" cy="78" rx="22" ry="16" fill={cloudColor} />
          <ellipse cx="106" cy="78" rx="20" ry="14" fill={cloudColor} />
          <rect x="40" y="70" width="86" height="24" fill={cloudColor} />
        </motion.g>
      )}
      {isRainy && [53, 67, 81, 95, 109].map((x, i) => (
        <motion.line key={x} x1={x} y1={100} x2={x - 4} y2={116} stroke="#60a5fa" strokeWidth="3" strokeLinecap="round" animate={{ y: [0, 8, 0], opacity: [0.5, 1, 0.5] }} transition={{ duration: 0.75, repeat: Infinity, delay: i * 0.14 }} />
      ))}
      {isStormy && (
        <motion.g animate={{ opacity: [1, 0.15, 1], scale: [1, 1.08, 1] }} transition={{ duration: 0.65, repeat: Infinity }} style={{ transformOrigin: "80px 112px" }}>
          <path d="M89 94 L73 120 L83 120 L71 144 L97 114 L85 114 Z" fill="#fbbf24" stroke="#f59e0b" strokeWidth="1.5" strokeLinejoin="round" />
        </motion.g>
      )}
      {isSnowy && [53, 67, 81, 95, 109].map((x, i) => (
        <motion.g key={x} animate={{ y: [0, 16, 0], rotate: [0, 360] }} transition={{ duration: 2.2, repeat: Infinity, delay: i * 0.38, ease: "easeInOut" }} style={{ transformOrigin: `${x}px 112px` }}>
          <circle cx={x} cy={112} r="3.5" fill="#bfdbfe" />
          <line x1={x - 5} y1={112} x2={x + 5} y2={112} stroke="#93c5fd" strokeWidth="1.5" strokeLinecap="round" />
          <line x1={x} y1={107} x2={x} y2={117} stroke="#93c5fd" strokeWidth="1.5" strokeLinecap="round" />
        </motion.g>
      ))}
      <rect x="38" y="136" width="84" height="18" rx="9" fill={isStormy ? "#334155" : isRainy ? "#3b82f6" : isSnowy ? "#93c5fd" : "#fbbf24"} fillOpacity="0.15" />
      <text x="80" y="149" fill={isStormy ? "#475569" : isRainy ? "#3b82f6" : isSnowy ? "#60a5fa" : "#d97706"} fontSize="10" fontWeight="bold" textAnchor="middle">
        {isSunny ? "Ensoleillé" : isCloudy ? "Nuageux" : isRainy ? "Pluvieux" : isStormy ? "Orageux" : "Neigeux"}
      </text>
    </svg>
  );
}

// ============================================================================
// 27. BATTERY LEVEL ILLUSTRATION
// ============================================================================

export interface BatteryLevelProps extends IllustrationProps {
  level?: number;
  charging?: boolean;
}

export function BatteryLevelIllustration({ size = 160, level = 75, charging = false, className, style, ...props }: BatteryLevelProps) {
  const pct = Math.min(100, Math.max(0, level));
  const color = pct <= 20 ? "#f43f5e" : pct <= 40 ? "#f97316" : pct <= 60 ? "#f59e0b" : "#10b981";
  const fillW = (pct / 100) * 80;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      <motion.g animate={{ y: [-3, 3, -3] }} transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}>
        <rect x="20" y="52" width="108" height="56" rx="10" fill="none" stroke="#e2e8f0" strokeWidth="4" className="dark:stroke-neutral-600" />
        <rect x="128" y="70" width="12" height="20" rx="4" fill="#e2e8f0" className="dark:fill-neutral-600" />
        <motion.rect x="28" y="60" height="40" rx="6" fill={color} animate={{ width: fillW, fill: color }} transition={{ duration: 0.7, ease: "easeOut" }} />
        {pct > 8 && <motion.rect x="28" y="64" height="7" rx="3" fill="#ffffff" opacity={0.28} animate={{ width: Math.max(0, fillW - 10) }} transition={{ duration: 0.7, ease: "easeOut" }} />}
        {charging && (
          <motion.g animate={{ opacity: [0.7, 1, 0.7] }} transition={{ duration: 0.8, repeat: Infinity }}>
            <path d="M86 64 L74 82 L82 82 L72 96 L94 74 L84 74 Z" fill="#ffffff" fillOpacity="0.9" />
          </motion.g>
        )}
      </motion.g>
      <text x="80" y="128" fill={color} fontSize="20" fontWeight="bold" textAnchor="middle">{pct}%</text>
      <text x="80" y="148" fill={charging ? "#f59e0b" : "#94a3b8"} fontSize="9" fontWeight={charging ? "bold" : "normal"} textAnchor="middle">
        {charging ? "⚡ En charge" : pct <= 20 ? "Critique !" : pct <= 40 ? "Faible" : pct <= 60 ? "Modéré" : "Bon"}
      </text>
    </svg>
  );
}

// ============================================================================
// 28. RATING STARS ILLUSTRATION
// ============================================================================

export interface RatingStarsProps extends IllustrationProps {
  rating?: 0 | 1 | 2 | 3 | 4 | 5;
  reviewCount?: number;
}

const STAR_LABELS = ["Sans note", "Médiocre", "Passable", "Bien", "Très bien", "Excellent"];

export function RatingStarsIllustration({ size = 160, rating = 4, reviewCount, className, style, ...props }: RatingStarsProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      <motion.g animate={{ rotate: [0, 5, -5, 0], scale: [1, 1.04, 1] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }} style={{ transformOrigin: "80px 62px" }}>
        <path d="M80 28 L90 56 L120 56 L97 73 L105 100 L80 83 L55 100 L63 73 L40 56 L70 56 Z" fill={rating >= 1 ? "#fbbf24" : "#e2e8f0"} stroke={rating >= 1 ? "#f59e0b" : "#cbd5e1"} strokeWidth="2" />
        {rating >= 1 && <path d="M80 28 L90 56 L120 56 L97 73 L105 100 L80 83 L55 100 L63 73 L40 56 L70 56 Z" fill="none" stroke="#ffffff" strokeWidth="1.5" opacity="0.35" />}
      </motion.g>
      <text x="80" y="62" fill={rating >= 1 ? "#92400e" : "#94a3b8"} fontSize="20" fontWeight="bold" textAnchor="middle">{rating}.0</text>
      <g transform="translate(13, 110)">
        {[0, 1, 2, 3, 4].map((i) => (
          <motion.g key={i} animate={i < rating ? { scale: [0.9, 1.1, 0.9] } : { scale: 0.85 }} transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.1 }} style={{ transformOrigin: `${13 + i * 27}px 14px` }}>
            <path d="M13 0 L16 9 L26 9 L18 15 L21 24 L13 18 L5 24 L8 15 L0 9 L10 9 Z" transform={`translate(${i * 27}, 0)`} fill={i < rating ? "#fbbf24" : "#e2e8f0"} stroke={i < rating ? "#f59e0b" : "#cbd5e1"} strokeWidth="1.5" />
          </motion.g>
        ))}
      </g>
      <text x="80" y="148" fill={rating >= 4 ? "#d97706" : "#64748b"} fontSize="9" fontWeight="bold" textAnchor="middle">
        {STAR_LABELS[rating]}{reviewCount != null ? ` · ${reviewCount} avis` : ""}
      </text>
    </svg>
  );
}

// ============================================================================
// 29. EMOJI MOOD ILLUSTRATION
// ============================================================================

export interface EmojiMoodProps extends IllustrationProps {
  mood?: 1 | 2 | 3 | 4 | 5;
}

const MOOD_COLORS_MAP: Record<number, string> = { 1: "#f43f5e", 2: "#f97316", 3: "#f59e0b", 4: "#84cc16", 5: "#10b981" };
const MOOD_LABELS_MAP: Record<number, string> = { 1: "Pas du tout", 2: "Peu satisfait", 3: "Neutre", 4: "Satisfait", 5: "Très satisfait" };
const MOUTH_PATHS: Record<number, string> = {
  1: "M62 84 Q 80 74, 98 84",
  2: "M65 83 Q 80 77, 95 83",
  3: "M65 80 L 95 80",
  4: "M65 76 Q 80 86, 95 76",
  5: "M60 74 Q 80 92, 100 74",
};

export function EmojiMoodIllustration({ size = 160, mood = 3, className, style, ...props }: EmojiMoodProps) {
  const color = MOOD_COLORS_MAP[mood] ?? "#f59e0b";
  const label = MOOD_LABELS_MAP[mood] ?? "Neutre";
  const mouthPath = MOUTH_PATHS[mood] ?? MOUTH_PATHS[3];

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      <motion.circle cx="80" cy="72" r="50" fill={color} fillOpacity="0.07" animate={{ scale: [0.95, 1.05, 0.95] }} transition={{ duration: 2.5, repeat: Infinity }} />
      <motion.g animate={{ y: [-3, 3, -3] }} transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}>
        <circle cx="80" cy="72" r="42" fill={color} fillOpacity="0.14" stroke={color} strokeWidth="3" />
        {mood <= 2 ? (
          <>
            <circle cx="66" cy="62" r="5" fill={color} />
            <circle cx="94" cy="62" r="5" fill={color} />
            <path d="M62 56 Q66 52 70 56" stroke={color} strokeWidth="2.5" strokeLinecap="round" fill="none" opacity="0.6" />
            <path d="M90 56 Q94 52 98 56" stroke={color} strokeWidth="2.5" strokeLinecap="round" fill="none" opacity="0.6" />
          </>
        ) : mood === 3 ? (
          <>
            <circle cx="66" cy="64" r="5" fill={color} />
            <circle cx="94" cy="64" r="5" fill={color} />
            <circle cx="68" cy="63" r="1.8" fill="#ffffff" opacity="0.6" />
            <circle cx="96" cy="63" r="1.8" fill="#ffffff" opacity="0.6" />
          </>
        ) : (
          <>
            <path d="M61 66 Q66 59 71 66" stroke={color} strokeWidth="3.5" strokeLinecap="round" fill="none" />
            <path d="M89 66 Q94 59 99 66" stroke={color} strokeWidth="3.5" strokeLinecap="round" fill="none" />
          </>
        )}
        {mood >= 4 && (
          <>
            <circle cx="56" cy="78" r="8" fill="#f43f5e" opacity="0.22" />
            <circle cx="104" cy="78" r="8" fill="#f43f5e" opacity="0.22" />
          </>
        )}
        {mood === 1 && (
          <motion.ellipse cx="68" cy="74" rx="2.5" ry="4" fill="#60a5fa" animate={{ y: [0, 4, 0] }} transition={{ duration: 1.2, repeat: Infinity }} />
        )}
        <path d={mouthPath} stroke={color} strokeWidth="3.5" strokeLinecap="round" fill="none" />
      </motion.g>
      <g>
        {[1, 2, 3, 4, 5].map((m) => (
          <motion.circle key={m} cx={36 + (m - 1) * 22} cy="128" r={m === mood ? 7 : 5} fill={MOOD_COLORS_MAP[m]} opacity={m === mood ? 1 : 0.28} animate={{ scale: m === mood ? [1, 1.15, 1] : 1 }} transition={{ duration: 1.2, repeat: Infinity }} />
        ))}
      </g>
      <text x="80" y="150" fill={color} fontSize="9" fontWeight="bold" textAnchor="middle">{label}</text>
    </svg>
  );
}

// ============================================================================
// 30. STEP PROGRESS ILLUSTRATION
// ============================================================================

export interface StepProgressProps extends IllustrationProps {
  step?: number;
  total?: 3 | 4 | 5;
}

export function StepProgressIllustration({ size = 160, step = 2, total = 4, className, style, ...props }: StepProgressProps) {
  const n = Math.min(5, Math.max(3, total));
  const cur = Math.min(n, Math.max(1, step));
  const spacing = 120 / (n - 1);
  const sx = 20;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      <line x1={sx} y1="65" x2={sx + 120} y2="65" stroke="#e2e8f0" strokeWidth="4" strokeLinecap="round" className="dark:stroke-neutral-700" />
      <motion.line x1={sx} y1="65" x2={sx + spacing * (cur - 1)} y2="65" stroke="#6366f1" strokeWidth="4" strokeLinecap="round" animate={{ x2: sx + spacing * (cur - 1) }} transition={{ duration: 0.5, ease: "easeOut" }} />
      {Array.from({ length: n }).map((_, i) => {
        const cx = sx + i * spacing;
        const done = i + 1 < cur;
        const active = i + 1 === cur;
        return (
          <motion.g key={i} animate={active ? { scale: [0.9, 1.12, 0.9] } : { scale: 1 }} transition={{ duration: 1.5, repeat: Infinity }} style={{ transformOrigin: `${cx}px 65px` }}>
            {active && <circle cx={cx} cy="65" r="19" fill="#6366f1" fillOpacity="0.14" />}
            <circle cx={cx} cy="65" r="13" fill={done || active ? "#6366f1" : "#ffffff"} stroke={done || active ? "#6366f1" : "#e2e8f0"} strokeWidth="3" className={!done && !active ? "dark:stroke-neutral-700 dark:fill-neutral-900" : ""} />
            {done ? (
              <path d={`M${cx - 5} 65 L${cx - 1} 69 L${cx + 6} 59`} stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            ) : (
              <text x={cx} y="69" fill={active ? "#ffffff" : "#94a3b8"} fontSize="10" fontWeight="bold" textAnchor="middle">{i + 1}</text>
            )}
          </motion.g>
        );
      })}
      <text x="80" y="100" fill="#6366f1" fontSize="12" fontWeight="bold" textAnchor="middle">Étape {cur} sur {n}</text>
      <rect x="28" y="112" width="104" height="14" rx="7" fill="#e2e8f0" />
      <motion.rect x="28" y="112" height="14" rx="7" fill="#6366f1" animate={{ width: (cur / n) * 104 }} transition={{ duration: 0.5 }} />
      <text x="80" y="138" fill="#64748b" fontSize="9" textAnchor="middle">{Math.round((cur / n) * 100)}% complété</text>
    </svg>
  );
}

// ============================================================================
// 31. SYNC STATUS ILLUSTRATION
// ============================================================================

export interface SyncStatusProps extends IllustrationProps {
  status?: "synced" | "syncing" | "error" | "offline";
}

export function SyncStatusIllustration({ size = 160, status = "synced", className, style, ...props }: SyncStatusProps) {
  const isSyncing = status === "syncing";
  const isError = status === "error";
  const isOffline2 = status === "offline";
  const isSynced = status === "synced";
  const color = isSynced ? "#10b981" : isError ? "#f43f5e" : isOffline2 ? "#64748b" : "#6366f1";

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      <motion.g animate={{ y: [-3, 3, -3] }} transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}>
        <ellipse cx="80" cy="64" rx="42" ry="26" fill={color} fillOpacity="0.1" stroke={color} strokeWidth="2.5" />
        <ellipse cx="54" cy="74" rx="22" ry="18" fill={color} fillOpacity="0.1" stroke={color} strokeWidth="2.5" />
        <ellipse cx="106" cy="74" rx="20" ry="15" fill={color} fillOpacity="0.1" stroke={color} strokeWidth="2.5" />
        <rect x="34" y="64" width="92" height="28" fill={color} fillOpacity="0.08" />
        {isSynced && (
          <motion.g initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 300 }}>
            <circle cx="80" cy="68" r="16" fill="#10b981" />
            <path d="M72 68 L78 74 L90 60" stroke="#ffffff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </motion.g>
        )}
        {isSyncing && (
          <motion.g animate={{ rotate: [0, 360] }} transition={{ duration: 1.1, repeat: Infinity, ease: "linear" }} style={{ transformOrigin: "80px 68px" }}>
            <path d="M80 52 A16 16 0 1 1 64 68" stroke="#6366f1" strokeWidth="4" strokeLinecap="round" fill="none" />
            <polygon points="80,46 87,57 73,57" fill="#6366f1" />
          </motion.g>
        )}
        {isError && (
          <motion.g animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 1, repeat: Infinity }}>
            <circle cx="80" cy="68" r="15" fill="#f43f5e" fillOpacity="0.15" stroke="#f43f5e" strokeWidth="2.5" />
            <line x1="74" y1="62" x2="86" y2="74" stroke="#f43f5e" strokeWidth="4" strokeLinecap="round" />
            <line x1="86" y1="62" x2="74" y2="74" stroke="#f43f5e" strokeWidth="4" strokeLinecap="round" />
          </motion.g>
        )}
        {isOffline2 && (
          <g>
            <circle cx="80" cy="68" r="15" fill="#64748b" fillOpacity="0.18" stroke="#94a3b8" strokeWidth="2.5" />
            <line x1="72" y1="68" x2="88" y2="68" stroke="#94a3b8" strokeWidth="4" strokeLinecap="round" />
          </g>
        )}
      </motion.g>
      <rect x="26" y="102" width="108" height="24" rx="12" fill={color} fillOpacity="0.12" stroke={color} strokeWidth="1.5" />
      <text x="80" y="118" fill={color} fontSize="10" fontWeight="bold" textAnchor="middle">
        {isSynced ? "✓ Synchronisé" : isSyncing ? "Synchronisation…" : isError ? "Erreur de sync" : "Hors ligne"}
      </text>
      {[38, 70, 102].map((x) => (
        <g key={x}>
          <rect x={x} y="136" width="20" height="13" rx="2" fill={color} fillOpacity="0.18" stroke={color} strokeWidth="1.5" />
          {isSynced && <circle cx={x + 10} cy="142.5" r="3" fill={color} />}
        </g>
      ))}
    </svg>
  );
}

// ============================================================================
// 32. CART ILLUSTRATION
// ============================================================================

export interface CartProps extends IllustrationProps {
  itemCount?: number;
}

export function CartIllustration({ size = 160, itemCount = 3, className, style, ...props }: CartProps) {
  const count = Math.min(10, Math.max(0, itemCount));
  const isEmpty = count === 0;
  const color = isEmpty ? "#94a3b8" : "#6366f1";

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      <motion.g animate={{ y: [-3, 3, -3] }} transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}>
        <path d="M46 36 C46 23, 114 23, 114 36" stroke={color} strokeWidth="5" strokeLinecap="round" fill="none" />
        <rect x="26" y="36" width="108" height="68" rx="12" fill={color} fillOpacity="0.09" stroke={color} strokeWidth="3" />
        <line x1="60" y1="36" x2="60" y2="104" stroke={color} strokeWidth="1.5" opacity="0.25" />
        <line x1="80" y1="36" x2="80" y2="104" stroke={color} strokeWidth="1.5" opacity="0.25" />
        <line x1="100" y1="36" x2="100" y2="104" stroke={color} strokeWidth="1.5" opacity="0.25" />
        {!isEmpty && (
          <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
            {count >= 1 && <rect x="34" y="46" width="20" height="18" rx="4" fill={color} fillOpacity="0.5" />}
            {count >= 2 && <rect x="64" y="46" width="20" height="18" rx="4" fill={color} fillOpacity="0.42" />}
            {count >= 3 && <rect x="84" y="46" width="20" height="18" rx="4" fill={color} fillOpacity="0.36" />}
            {count >= 4 && <rect x="34" y="70" width="20" height="18" rx="4" fill={color} fillOpacity="0.46" />}
            {count >= 5 && <rect x="64" y="70" width="20" height="18" rx="4" fill={color} fillOpacity="0.38" />}
            {count >= 6 && <rect x="84" y="70" width="20" height="18" rx="4" fill={color} fillOpacity="0.3" />}
            {count >= 7 && <text x="108" y="60" fill={color} fontSize="10" fontWeight="bold" textAnchor="middle">+{count - 6}</text>}
          </motion.g>
        )}
        {isEmpty && (
          <motion.text x="80" y="75" fill="#94a3b8" fontSize="11" textAnchor="middle" animate={{ opacity: [0.4, 0.9, 0.4] }} transition={{ duration: 2, repeat: Infinity }}>Vide</motion.text>
        )}
        <circle cx="56" cy="120" r="10" fill={color} fillOpacity="0.18" stroke={color} strokeWidth="3" />
        <circle cx="104" cy="120" r="10" fill={color} fillOpacity="0.18" stroke={color} strokeWidth="3" />
        <circle cx="56" cy="120" r="3.5" fill={color} />
        <circle cx="104" cy="120" r="3.5" fill={color} />
      </motion.g>
      {!isEmpty && (
        <motion.g initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 450 }}>
          <circle cx="120" cy="32" r="15" fill="#f43f5e" stroke="#ffffff" strokeWidth="2.5" />
          <text x="120" y="37" fill="#ffffff" fontSize={count >= 10 ? "8" : "12"} fontWeight="bold" textAnchor="middle">{count}</text>
        </motion.g>
      )}
    </svg>
  );
}

// ============================================================================
// 33. PRICING TIER ILLUSTRATION
// ============================================================================

export interface PricingTierProps extends IllustrationProps {
  tier?: "free" | "pro" | "enterprise";
}

export function PricingTierIllustration({ size = 160, tier = "pro", className, style, ...props }: PricingTierProps) {
  const isFree = tier === "free";
  const isPro = tier === "pro";
  const isEnt = tier === "enterprise";
  const color = isFree ? "#64748b" : isPro ? "#6366f1" : "#f59e0b";
  const label = isFree ? "Free" : isPro ? "Pro" : "Enterprise";

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 160 160"
      fill="none"
      className={cn("inline-block shrink-0 align-middle", className)}
      style={{ width: typeof size === "number" ? `${size}px` : size, height: typeof size === "number" ? `${size}px` : size, overflow: "visible", ...style }}
      {...props}
    >
      <motion.circle cx="80" cy="68" r="52" fill={color} fillOpacity="0.06" animate={{ scale: [0.95, 1.05, 0.95] }} transition={{ duration: 3, repeat: Infinity }} />
      {isFree && (
        <motion.g animate={{ y: [-3, 3, -3] }} transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}>
          <line x1="80" y1="104" x2="80" y2="48" stroke="#94a3b8" strokeWidth="4" strokeLinecap="round" />
          <path d="M80 75 C80 75, 58 70, 48 52 C64 52 78 64 80 75" fill="#94a3b8" />
          <path d="M80 62 C80 62, 102 57, 112 39 C96 39 82 53 80 62" fill="#64748b" />
          <ellipse cx="80" cy="104" rx="16" ry="5" fill="#94a3b8" fillOpacity="0.4" />
        </motion.g>
      )}
      {isPro && (
        <motion.g animate={{ rotate: [0, 8, -8, 0], scale: [1, 1.04, 1] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }} style={{ transformOrigin: "80px 66px" }}>
          <path d="M80 36 L89 62 L116 62 L94 79 L102 105 L80 88 L58 105 L66 79 L44 62 L71 62 Z" fill="#6366f1" fillOpacity="0.25" stroke="#6366f1" strokeWidth="2.5" strokeLinejoin="round" />
          <path d="M80 44 L88 64 L108 64 L92 76 L98 96 L80 84 L62 96 L68 76 L52 64 L72 64 Z" fill="#6366f1" />
          <path d="M74 58 L78 68 L74 78" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.45" />
          <motion.circle cx="114" cy="44" r="3.5" fill="#8b5cf6" animate={{ scale: [0.5, 1.5, 0.5], opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.5, repeat: Infinity }} />
          <motion.circle cx="46" cy="94" r="2.5" fill="#6366f1" animate={{ scale: [0.5, 1.5, 0.5], opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.5, repeat: Infinity, delay: 0.5 }} />
        </motion.g>
      )}
      {isEnt && (
        <motion.g animate={{ y: [-2, 2, -2] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}>
          <rect x="52" y="50" width="56" height="56" rx="3" fill="#f59e0b" fillOpacity="0.14" stroke="#f59e0b" strokeWidth="2.5" />
          <rect x="64" y="32" width="32" height="74" rx="3" fill="#f59e0b" fillOpacity="0.2" stroke="#f59e0b" strokeWidth="2.5" />
          <polygon points="80,18 90,32 70,32" fill="#f59e0b" />
          {[[67, 48], [79, 48], [91, 48], [67, 62], [79, 62], [91, 62], [67, 76], [79, 76], [91, 76]].map(([x, y], i) => (
            <motion.rect key={i} x={x} y={y} width="8" height="8" rx="1" fill="#f59e0b" animate={{ opacity: [0.25, 1, 0.25] }} transition={{ duration: 2, repeat: Infinity, delay: i * 0.18 }} />
          ))}
        </motion.g>
      )}
      <rect x="30" y="118" width="100" height="26" rx="13" fill={color} />
      <text x="80" y="136" fill="#ffffff" fontSize="12" fontWeight="bold" textAnchor="middle">{label}</text>
      <g>{[0, 1, 2].map((i) => (
        <circle key={i} cx={70 + i * 10} cy="152" r={3.5} fill={i < (isFree ? 1 : isPro ? 2 : 3) ? color : "#e2e8f0"} />
      ))}</g>
    </svg>
  );
}
